// AuthProvider holds the signed-in session and the user's accounts, and exposes
// them through a React context so any component can call `useAuth()`.
//
// Responsibilities (kept deliberately small):
//   - Track the Supabase auth session (login / logout / refresh).
//   - Load the accounts the user belongs to.
//   - Track which account is "active" (the tenant we're currently viewing).

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase, isSupabaseConfigured } from "../lib/supabase";
import type { Membership } from "../lib/types";

interface AuthState {
  session: Session | null;
  /** True while we're still figuring out if the user is logged in. */
  loading: boolean;
  /** True while the signed-in user's accounts are still loading. */
  accountsLoading: boolean;
  accounts: Membership[];
  activeAccount: Membership | null;
  setActiveAccountId: (id: string) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/** Load the accounts the given user can access (joined with the role + name). */
async function fetchMemberships(userId: string): Promise<Membership[]> {
  const { data, error } = await supabase
    .from("account_users")
    .select("role, account_id, accounts(id, name)")
    .eq("user_id", userId);
  if (error) throw error;
  return (data || []).map((r: any) => ({
    accountId: r.account_id,
    role: r.role,
    name: r.accounts?.name ?? "Account",
  }));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accounts, setAccounts] = useState<Membership[]>([]);
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null);

  // Watch the auth session.
  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  // Whenever the user changes, (re)load their accounts.
  useEffect(() => {
    const uid = session?.user?.id;
    if (!uid) {
      setAccounts([]);
      setActiveAccountId(null);
      setAccountsLoading(false);
      return;
    }
    setAccountsLoading(true);
    fetchMemberships(uid)
      .then((m) => {
        setAccounts(m);
        setActiveAccountId((prev) => prev ?? m[0]?.accountId ?? null);
      })
      .catch((e) => console.error("Failed to load accounts:", e))
      .finally(() => setAccountsLoading(false));
  }, [session?.user?.id]);

  const activeAccount = useMemo(
    () => accounts.find((a) => a.accountId === activeAccountId) ?? null,
    [accounts, activeAccountId]
  );

  const value: AuthState = {
    session,
    loading,
    accountsLoading,
    accounts,
    activeAccount,
    setActiveAccountId,
    signOut: async () => {
      await supabase.auth.signOut();
      setActiveAccountId(null);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Access auth state anywhere in the tree. Throws if used outside the provider. */
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
